import React, { useState } from 'react';
import { Users, ChevronDown, Search, X, Mail, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import type { Band } from '../../types/database';

interface BandMultiSelectProps {
  bands: Band[];
  selectedBands: Band[];
  onSelectBands: (bands: Band[]) => void;
  onInviteBand?: (bandName: string, email: string) => Promise<void>;
  placeholder?: string;
  error?: string;
}

export function BandMultiSelect({
  bands,
  selectedBands,
  onSelectBands,
  onInviteBand,
  placeholder = 'Select bands...',
  error
}: BandMultiSelectProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteBandName, setInviteBandName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const filteredBands = bands.filter(band =>
    !debouncedSearch ||
    band.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    band.location?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const handleBandToggle = (band: Band) => {
    const isSelected = selectedBands.some(b => b.id === band.id);
    if (isSelected) {
      onSelectBands(selectedBands.filter(b => b.id !== band.id));
    } else {
      onSelectBands([...selectedBands, band]);
    }
  };

  const handleRemoveBand = (bandId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectBands(selectedBands.filter(b => b.id !== bandId));
  };

  const handleInviteSubmit = async () => {
    if (!onInviteBand || !inviteBandName.trim() || !inviteEmail.trim()) {
      return;
    }

    try {
      setIsInviting(true);
      await onInviteBand(inviteBandName.trim(), inviteEmail.trim());
      setInviteBandName('');
      setInviteEmail('');
      setShowInviteForm(false);
      setShowDropdown(false);
    } catch (error) {
      // Error is handled by parent component
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <div
      className="relative"
      onBlur={(e) => {
        if (!e.relatedTarget?.closest('.band-multi-select-dropdown')) {
          setShowDropdown(false);
          setShowInviteForm(false);
        }
      }}
    >
      <div className="w-full">
        <div
          onClick={() => setShowDropdown(!showDropdown)}
          className={`w-full px-4 py-2 bg-dark border rounded-lg text-light flex items-center justify-between hover:border-gray-custom/40 transition-colors min-h-[42px] cursor-pointer ${
            error ? 'border-red-500/40' : 'border-gray-custom/20'
          }`}
        >
          <div className="flex items-center flex-wrap gap-2 flex-1">
            <Users className="w-5 h-5 text-light/40 flex-shrink-0" />
            {selectedBands.length === 0 ? (
              <span className="text-light/60">{placeholder}</span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedBands.map((band) => (
                  <span
                    key={band.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded"
                  >
                    {band.name}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveBand(band.id, e)}
                      className="hover:bg-light/10 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <ChevronDown className={`w-5 h-5 text-light/40 transition-transform flex-shrink-0 ml-2 ${showDropdown ? 'transform rotate-180' : ''}`} />
        </div>

        {showDropdown && (
          <div className="band-multi-select-dropdown absolute z-[100] w-full mt-1 bg-[#232323] border border-gray-custom/20 rounded-lg shadow-lg">
            <div className="p-3 border-b border-gray-custom/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Suche Band..."
                  className="w-full pl-9 pr-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                  autoFocus
                />
              </div>
            </div>

            {!showInviteForm ? (
              <>
                <div className="max-h-64 overflow-y-auto">
                  {filteredBands.map((band) => {
                    const isSelected = selectedBands.some(b => b.id === band.id);
                    return (
                      <button
                        key={band.id}
                        type="button"
                        onClick={() => handleBandToggle(band)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-custom/10 text-light/80 flex items-center space-x-3 ${
                          isSelected ? 'bg-light/10' : ''
                        }`}
                      >
                        <div className="w-10 h-10 bg-gray-custom/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {band.avatar_url ? (
                            <img
                              src={band.avatar_url}
                              alt={band.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <Users className="w-4 h-4 text-light/60" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{band.name}</div>
                          {band.location && (
                            <div className="text-sm text-light/60 truncate">{band.location}</div>
                          )}
                          {band.genres && band.genres.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              {band.genres.slice(0, 2).map((genre) => (
                                <span
                                  key={genre}
                                  className="px-1.5 py-0.5 bg-tertiary rounded text-xs text-light"
                                >
                                  {genre}
                                </span>
                              ))}
                              {band.genres.length > 2 && (
                                <span className="text-xs text-light/40">
                                  +{band.genres.length - 2}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 bg-dark/80 rounded-full flex items-center justify-center flex-shrink-0">
                            <svg className="w-3 h-3 text-light" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}

                  {filteredBands.length === 0 && (
                    <div className="px-4 py-6 text-light/40 text-center">
                      {debouncedSearch ? 'Keine Bands gefunden' : 'Keine Bands verfügbar'}
                    </div>
                  )}
                </div>

                {onInviteBand && (
                  <div className="p-3 border-t border-gray-custom/10">
                    <button
                      type="button"
                      onClick={() => setShowInviteForm(true)}
                      className="w-full px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-light flex items-center justify-center gap-2 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Band per E-Mail einladen</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-light/80 mb-2">
                    Bandname
                  </label>
                  <input
                    type="text"
                    value={inviteBandName}
                    onChange={(e) => setInviteBandName(e.target.value)}
                    placeholder="Name der Band"
                    className="w-full px-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-light/80 mb-2">
                    E-Mail Adresse
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="info@band.ch"
                    className="w-full px-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className="flex-1 px-4 py-2 bg-dark border border-gray-custom/20 hover:bg-gray-custom/10 rounded-lg text-light transition-colors"
                    disabled={isInviting}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleInviteSubmit}
                    className="flex-1 px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-light transition-colors disabled:opacity-50"
                    disabled={isInviting}
                  >
                    {isInviting ? 'Sende...' : 'Einladung senden'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}
